import { Delete, Download, MoreVert, Print } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Typography,
} from "@mui/material";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import { useToast } from "contexts/ToastContext";
import {
  useAdminDeleteProcedure,
  useAdminProceduresByPatient,
  useAdminToggleProcedureVisibility,
} from "hooks/patientDetail/useAdminProcedure";
import type { ProcedureGenerationOutputDto } from "libs/cellera-sdk";
import React, { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  formatDateForFileDisplay,
  formatDateForFileName,
  formatDateTimeForDisplay,
  generateFileName,
  getFileIcon,
  handleFileDownload,
} from "utils/protocol-helpers";
import { handlePrintFile } from "utils/printHelpers";

import DeleteProtocolModal from "./DeleteProtocolModal";

const openLinkInNewTab = (url: string | undefined | null): void => {
  if (url == null || url === "") return;
  window.open(url, "_blank", "noopener,noreferrer");
};

interface AdminProceduresProps {
  patientId: string;
  firstName: string;
  lastName: string;
}

interface ProcedureFile {
  type: "DOCX" | "PDF";
  url: string | undefined;
  fileName: string;
}

interface Procedure {
  id: string;
  date: Date;
  displayLabel: string;
  files: ProcedureFile[];
  isHiddenFromPatient: boolean;
}

interface ToggleTarget {
  procedureId: string;
  nextIsHidden: boolean;
}

type MenuAnchorState = { el: HTMLElement; procedureId: string } | null;

interface ProcedureFileCardRowProps {
  file: ProcedureFile;
  procedureDate: Date;
  parentId: string;
  fileIndex: number;
  getFileIconColor: (type: "DOCX" | "PDF") => string;
  printingFileKey: string | null;
  onPrint: (fileKey: string, url: string | undefined, fileName: string) => void;
}

function ProcedureFileCardRow({
  file,
  procedureDate,
  parentId,
  fileIndex,
  getFileIconColor,
  printingFileKey,
  onPrint,
}: ProcedureFileCardRowProps) {
  const { colors } = useCelleraTheme();
  const fileKey = `${parentId}-${fileIndex}`;
  const IconComponent = getFileIcon(file.type);
  const iconColor = getFileIconColor(file.type);
  const fileDate = formatDateForFileDisplay(procedureDate);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 48,
          height: 48,
          flexShrink: 0,
          bgcolor: colors.background.secondary,
          borderRadius: "4px",
        }}
      >
        <IconComponent sx={{ fontSize: 32, color: iconColor }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: colors.text.primary,
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          onClick={() => openLinkInNewTab(file.url)}
          className="cursor-pointer"
        >
          {file.fileName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            display: "block",
            mb: 0.5,
          }}
        >
          {fileDate}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            display: "block",
          }}
        >
          {file.type} Format
        </Typography>
      </Box>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onPrint(fileKey, file.url, file.fileName);
        }}
        size="small"
        disabled={printingFileKey === fileKey}
        sx={{
          color: colors.text.secondary,
          flexShrink: 0,
          "&:hover": {
            bgcolor: colors.background.default,
          },
          "&:disabled": {
            opacity: 0.7,
          },
        }}
        title={
          printingFileKey === fileKey
            ? "Preparing to print..."
            : "Print document"
        }
      >
        {printingFileKey === fileKey ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <Print />
        )}
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleFileDownload(file.url, file.fileName);
        }}
        size="small"
        sx={{
          color: colors.primary.main,
          flexShrink: 0,
          "&:hover": {
            bgcolor: colors.primary.light,
          },
        }}
      >
        <Download />
      </IconButton>
    </Box>
  );
}

interface ProcedureDocumentCardProps {
  item: Procedure;
  menuAnchor: MenuAnchorState;
  onMenuButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMenuClose: () => void;
  onDeleteFromMenu: () => void;
  onToggleVisibility: () => void;
  getFileIconColor: (type: "DOCX" | "PDF") => string;
  printingFileKey: string | null;
  onPrintFile: (
    fileKey: string,
    url: string | undefined,
    fileName: string,
  ) => void;
}

function ProcedureDocumentCard({
  item,
  menuAnchor,
  onMenuButtonClick,
  onMenuClose,
  onDeleteFromMenu,
  onToggleVisibility,
  getFileIconColor,
  printingFileKey,
  onPrintFile,
}: ProcedureDocumentCardProps) {
  const { colors } = useCelleraTheme();
  const displayDateTime = formatDateTimeForDisplay(item.date);
  const isVisible = !item.isHiddenFromPatient;
  const isMenuOpen = !!menuAnchor && menuAnchor.procedureId === item.id;

  return (
    <Card
      sx={{
        mb: 2,
        border: `1px solid ${colors.border.light}`,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ flex: 1, pb: "8px !important" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                lineHeight: 1.4,
              }}
            >
              {item.displayLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.secondary }}>
              Created on {displayDateTime}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onMenuButtonClick}
            sx={{
              color: colors.text.secondary,
              flexShrink: 0,
              "&:hover": { bgcolor: colors.background.secondary },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor?.el}
            open={isMenuOpen}
            onClose={onMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: { sx: { borderRadius: "8px", minWidth: 140 } },
            }}
          >
            <MenuItem
              onClick={() => {
                onMenuClose();
                onDeleteFromMenu();
              }}
              sx={{ color: colors.error[500], gap: 1 }}
            >
              <Delete fontSize="small" />
              Delete
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {item.files.map((file, fileIndex) => (
            <ProcedureFileCardRow
              key={`${item.id}-${fileIndex}`}
              file={file}
              procedureDate={item.date}
              parentId={item.id}
              fileIndex={fileIndex}
              getFileIconColor={getFileIconColor}
              printingFileKey={printingFileKey}
              onPrint={onPrintFile}
            />
          ))}
        </Box>
      </CardContent>

      <Box sx={{ px: 2, py: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isVisible}
              onChange={onToggleVisibility}
              size="small"
              color="primary"
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{
                color: isVisible ? colors.primary.main : colors.text.secondary,
                fontWeight: 500,
              }}
            >
              The patient can see this document
            </Typography>
          }
        />
      </Box>
    </Card>
  );
}

const AdminProcedures: React.FC<AdminProceduresProps> = ({
  patientId,
  firstName,
  lastName,
}) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [toggleTarget, setToggleTarget] = useState<ToggleTarget | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchorState>(null);
  const [printingFileKey, setPrintingFileKey] = useState<string | null>(null);

  const {
    procedures: proceduresData,
    pagination,
    isLoading,
  } = useAdminProceduresByPatient({
    patientId,
    page,
    pageSize,
  });

  const deleteMutation = useAdminDeleteProcedure(patientId);
  const toggleVisibilityMutation = useAdminToggleProcedureVisibility(patientId);

  const abbreviation =
    firstName && lastName
      ? `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`
      : "XX";

  const procedures = useMemo<Procedure[]>(() => {
    const items: Procedure[] = [];

    proceduresData.forEach((procedure: ProcedureGenerationOutputDto) => {
      const dateStr = formatDateForFileName(procedure.createdAt);
      const date = new Date(procedure.createdAt);

      const files: ProcedureFile[] = [];

      if (procedure.procedureTxtUrl) {
        files.push({
          type: "DOCX",
          url: procedure.procedureTxtUrl as unknown as string,
          fileName: generateFileName(abbreviation, dateStr, "docx"),
        });
      }

      if (procedure.procedurePdfUrl) {
        files.push({
          type: "PDF",
          url: procedure.procedurePdfUrl as unknown as string,
          fileName: generateFileName(abbreviation, dateStr, "pdf"),
        });
      }

      if (files.length > 0) {
        items.push({
          id: procedure.procedureId,
          date,
          displayLabel: `${abbreviation}${dateStr}`,
          files,
          isHiddenFromPatient: procedure.isHiddenFromPatient,
        });
      }
    });

    items.sort((a, b) => b.date.getTime() - a.date.getTime());

    const dateGroups = new Map<string, Procedure[]>();
    items.forEach((item) => {
      const dateKey = formatDateForFileName(item.date);
      const group = dateGroups.get(dateKey);
      if (group) {
        group.push(item);
      } else {
        dateGroups.set(dateKey, [item]);
      }
    });

    const result: Procedure[] = [];
    dateGroups.forEach((group) => {
      if (group.length === 1) {
        const item = group[0];
        const dateStr = formatDateForFileName(item.date);
        const updatedFiles = item.files.map((file) => ({
          ...file,
          fileName: generateFileName(
            abbreviation,
            dateStr,
            file.type.toLowerCase() as "docx" | "pdf",
          ),
        }));
        result.push({
          ...item,
          displayLabel: `${abbreviation}${dateStr}`,
          files: updatedFiles,
        });
      } else {
        group.forEach((item, index) => {
          const dateStr = formatDateForFileName(item.date);
          const suffix = index + 1;
          const updatedFiles = item.files.map((file) => ({
            ...file,
            fileName: generateFileName(
              abbreviation,
              dateStr,
              file.type.toLowerCase() as "docx" | "pdf",
              suffix,
            ),
          }));
          result.push({
            ...item,
            displayLabel: `${abbreviation}${dateStr} (${suffix})`,
            files: updatedFiles,
          });
        });
      }
    });

    return result.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [proceduresData, abbreviation]);

  const handlePrintProcedureFile = useCallback(
    (fileKey: string, url: string | undefined, fileName: string) => {
      handlePrintFile(url, fileName, {
        onStart: () => setPrintingFileKey(fileKey),
        onComplete: () => setPrintingFileKey(null),
        onError: () => {
          setPrintingFileKey(null);
          showError(
            "Print Error",
            "Failed to print document. Opening in new tab instead.",
          );
        },
      });
    },
    [showError],
  );

  const getFileIconColor = useCallback(
    (type: "DOCX" | "PDF") => {
      switch (type) {
        case "DOCX":
          return colors.info[500];
        case "PDF":
          return colors.error[500];
        default:
          return colors.text.secondary;
      }
    },
    [colors],
  );

  const handleToggleClick = (procedureId: string, currentIsHidden: boolean) => {
    setToggleTarget({ procedureId, nextIsHidden: !currentIsHidden });
  };

  const handleToggleConfirm = () => {
    if (!toggleTarget) return;
    toggleVisibilityMutation.mutate(
      {
        procedureId: toggleTarget.procedureId,
        isHiddenFromPatient: toggleTarget.nextIsHidden,
      },
      {
        onSettled: () => setToggleTarget(null),
      },
    );
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (procedures.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontStyle: "italic" }}
          >
            No procedures found for this patient
          </Typography>
        </Box>
      );
    }

    const totalPages = Math.ceil((pagination?.total || 0) / pageSize);

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
            },
            gap: 3,
          }}
        >
          {procedures.map((item) => (
            <ProcedureDocumentCard
              key={item.id}
              item={item}
              menuAnchor={menuAnchor}
              onMenuButtonClick={(e) =>
                setMenuAnchor({ el: e.currentTarget, procedureId: item.id })
              }
              onMenuClose={() => setMenuAnchor(null)}
              onDeleteFromMenu={() => setDeleteTarget(item.id)}
              onToggleVisibility={() =>
                handleToggleClick(item.id, item.isHiddenFromPatient)
              }
              getFileIconColor={getFileIconColor}
              printingFileKey={printingFileKey}
              onPrintFile={handlePrintProcedureFile}
            />
          ))}
        </Box>

        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </>
    );
  };

  const toggleConfirmMessage = toggleTarget?.nextIsHidden
    ? "Are you sure you want to hide this procedure from the patient? The patient will no longer be able to see this document."
    : "Are you sure you want to make this procedure visible to the patient? The patient will be able to see this document.";

  return (
    <Box sx={{ p: 3 }}>
      {renderContent()}

      <DeleteProtocolModal
        open={!!deleteTarget}
        type="procedure"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget, {
            onSettled: () => setDeleteTarget(null),
          });
        }}
        loading={deleteMutation.isPending}
      />

      <ConfirmationPopup
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
        title={
          toggleTarget?.nextIsHidden
            ? "Hide from Patient"
            : "Make Visible to Patient"
        }
        message={toggleConfirmMessage}
        confirmText={toggleTarget?.nextIsHidden ? "Hide" : "Make Visible"}
        cancelText="Cancel"
        confirmColor={
          toggleTarget?.nextIsHidden ? colors.warning[600] : colors.primary.main
        }
        loading={toggleVisibilityMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />
    </Box>
  );
};

export default AdminProcedures;
